<script setup lang="ts">
/**
 * Carbon-styled button (white theme).
 * Variants: primary, secondary, tertiary, ghost, danger.
 * Modifiers: icon (square 40px), small (32px height), block (full width).
 *
 * Designed to match the `.btn` family in the design bundle's
 * `partner-app/styles.css`. The primary variant intentionally renders a
 * 40px row with an inline trailing icon slot — this is the prototype's
 * "Crea / Salva" CTA pattern.
 */
import { computed } from 'vue'

type Variant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    icon?: boolean
    block?: boolean
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
  }>(),
  { variant: 'primary', size: 'md', icon: false, block: false, type: 'button', disabled: false },
)

defineEmits<{ click: [event: MouseEvent] }>()

const classes = computed(() => [
  'fz-btn',
  `fz-btn--${props.variant}`,
  props.size === 'sm' ? 'fz-btn--sm' : null,
  props.icon ? 'fz-btn--icon' : null,
  props.block ? 'fz-btn--block' : null,
])
</script>

<template>
  <button
    :class="classes"
    :type="type"
    :disabled="disabled"
    @click="(event) => $emit('click', event)"
  >
    <slot />
  </button>
</template>

<style scoped>
.fz-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-03);
  height: var(--size-md);
  padding: 0 var(--spacing-05);
  font-family: var(--font-sans);
  font-size: var(--fs-14);
  font-weight: var(--fw-regular);
  line-height: 1;
  border: none;
  cursor: pointer;
  border-radius: 0;
  white-space: nowrap;
  transition:
    background var(--dur-fast-02) var(--ease-productive),
    color var(--dur-fast-02) var(--ease-productive);
}
.fz-btn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
  box-shadow: inset 0 0 0 1px var(--focus-inset);
}
.fz-btn:disabled {
  background: var(--button-disabled);
  color: var(--text-on-color-disabled);
  cursor: not-allowed;
}

.fz-btn--primary {
  background: var(--button-primary);
  color: var(--text-on-color);
  padding-right: var(--spacing-04);
  justify-content: space-between;
  min-width: 160px;
}
.fz-btn--primary:hover:not(:disabled) {
  background: var(--button-primary-hover);
}

.fz-btn--secondary {
  background: var(--button-secondary);
  color: var(--text-on-color);
}
.fz-btn--secondary:hover:not(:disabled) {
  background: var(--button-secondary-hover);
}

.fz-btn--tertiary {
  background: transparent;
  color: var(--button-tertiary);
  box-shadow: inset 0 0 0 1px var(--button-tertiary);
}
.fz-btn--tertiary:hover:not(:disabled) {
  background: var(--button-tertiary);
  color: var(--text-on-color);
}

.fz-btn--ghost {
  background: transparent;
  color: var(--link-primary);
}
.fz-btn--ghost:hover:not(:disabled) {
  background: var(--bg-hover);
}

.fz-btn--danger {
  background: var(--button-danger-primary);
  color: var(--text-on-color);
}
.fz-btn--danger:hover:not(:disabled) {
  background: var(--button-danger-hover);
}

.fz-btn--icon {
  width: var(--size-md);
  height: var(--size-md);
  padding: 0;
  justify-content: center;
  min-width: 0;
}
.fz-btn--sm {
  height: var(--size-sm);
}
.fz-btn--block {
  display: flex;
  width: 100%;
}
</style>
